import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import css from './CreativePopup.css';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { CreativeType, CreativeTypes } from '../../types';
import CreativeAsset from '../creativeAsset/CreativeAsset';
import CreativePopupDimensionTags from '../creativePopupDimensionTags/CreativeDimensionTags';
import CreativePopupTabs from '../creativePopupTabs/CreativePopupTabs';
import { UNKNOWN_INDICATION } from '../../creativesFormattingUtils';
import { GeneralPopup, PopupTypes } from '../../../components/partials';
import useCsvDownload from '../../useCsvDownload';
import Icon, { IconTypes } from '../../../components/foundations/Icon';
import Tooltip from '../../../components/molecules/Tooltip';
import SubPageHeader from '../../../components/molecules/SubPageHeader';

export const GO_BACK_BUTTON_TEXT = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.GO_BACK';
export const CREATIVE_POPUP_TITLE = 'STATIC.PAGES.CREATIVE_GALLERY.CREATIVE_POPUP.TITLE';
export const DOWNLOAD_TOOLTIP_TITLE = 'STATIC.BUTTONS.DOWNLOAD_CSV';

export const formatValues = values => (values.length ? values.join(', ') : UNKNOWN_INDICATION);

const ASSET_ASPECT_RATIO = 550 / 400;
export const getTranslationKey = key => `STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.${key}`;

const CreativePopup = ({
    app,
    source,
    creative,
    selectedMetrics,
    selectedCohorts,
    dateRange,
    onClose: onCloseProp,
    isLoadingMetrics = false,
    isLoadingCohorts = false,
}) => {
    const assetRef = useRef();
    const [videoDuration, setVideoDuration] = useState();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showGoBackConfirmation, setShowGoBackConfirmation] = useState(false);

    const { assetIdList, assetNameList, creativeIdList, creativeNameList } = creative.multiValuedDimensions;
    const isAssetName = !!assetNameList?.length;
    const isAssetId = !!assetIdList?.length;

    const { onCsvDownloadClicked, isLoadingCreativesTags } = useCsvDownload({
        creatives: [creative],
        selectedAppOption: app,
        selectedSourceOption: source,
        dateRange,
        selectedCohorts,
        selectedTab: null,
        insightsDownloadUrl: null,
        isPopupDownload: true,
    });

    const handleClose = useCallback(() => {
        if (creative.type === CreativeTypes.PLAYABLE && assetRef.current) assetRef.current.style.pointerEvents = 'none';
        onCloseProp();
    }, [creative.type, onCloseProp]);

    const onClose = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowGoBackConfirmation(true);
        } else {
            handleClose();
        }
    }, [hasUnsavedChanges, handleClose]);

    const handleConfirmClose = () => {
        setShowGoBackConfirmation(false);
        handleClose();
    };

    const handleCancelClose = () => {
        setShowGoBackConfirmation(false);
    };

    const closeOnEscape = useCallback(event => event.key === 'Escape' && onClose(), [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', closeOnEscape, false);
        return () => document.removeEventListener('keydown', closeOnEscape, false);
    }, [closeOnEscape]);

    useEffect(() => {
        const handleBeforeUnload = event => {
            if (hasUnsavedChanges) {
                event.preventDefault();
                event.returnValue = '';
                alert('You have unsaved changes. Are you sure you want to leave?');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges]);

    const onLoad = () => {
        if (creative.type === CreativeTypes.VIDEO) setVideoDuration(assetRef.current?.duration);
    };

    return (
        <div className={css.creativePopup}>
            <GeneralPopup
                open={showGoBackConfirmation}
                type={PopupTypes.INFO_WITH_CANCEL}
                title="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.TITLE"
                text="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.MESSAGE"
                acceptText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.ACCEPT"
                rejectText="STATIC.PAGES.CREATIVE_GALLERY.TAGS_UPDATE.DISCARD_CONFIRMATION.REJECT"
                onAccept={handleConfirmClose}
                onReject={handleCancelClose}
            />
            <SubPageHeader
                onGoBack={onClose}
                goBackTranslationKey={GO_BACK_BUTTON_TEXT}
                labelTranslationKey={CREATIVE_POPUP_TITLE}
                className={css.header}
                rightSection={
                    <Icon
                        name="file-download"
                        onClick={onCsvDownloadClicked}
                        disabled={isLoadingCreativesTags}
                        hoverText={DOWNLOAD_TOOLTIP_TITLE}
                    />
                }
            />
            <div className={css.content}>
                <div className={css.creativeData}>
                    <CreativePopupDimensionTags
                        creative={creative}
                        source={source}
                        app={app}
                        videoDuration={videoDuration}
                    />
                    <CreativeAsset
                        ref={assetRef}
                        type={creative.type}
                        url={creative.url}
                        embeddingUrl={creative.embeddingUrl}
                        className={css.creativeAsset}
                        controls
                        aspectRatio={ASSET_ASPECT_RATIO}
                        onLoad={onLoad}
                        muted={false}
                    />
                    <div className={css.creativeRawData}>
                        <Translate
                            id={getTranslationKey(`${isAssetName ? 'ASSET' : 'CREATIVE'}_NAME`)}
                            data={{ value: formatValues(isAssetName ? assetNameList : creativeNameList || []) }}
                        />
                        <Translate
                            id={getTranslationKey(`${isAssetId ? 'ASSET' : 'CREATIVE'}_ID`)}
                            data={{ value: formatValues(isAssetId ? assetIdList : creativeIdList || []) }}
                        />
                    </div>
                </div>
                <CreativePopupTabs
                    app={app.value}
                    source={source?.label}
                    imageHash={creative.imageHash}
                    metrics={creative.metrics}
                    cohorts={creative.cohorts}
                    selectedMetrics={selectedMetrics}
                    selectedCohorts={selectedCohorts}
                    dateRange={dateRange}
                    onHasUnsavedChanges={setHasUnsavedChanges}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isLoadingMetrics={isLoadingMetrics}
                    isLoadingCohorts={isLoadingCohorts}
                />
            </div>
        </div>
    );
};

CreativePopup.propTypes = {
    creative: PropTypes.shape(CreativeType).isRequired,
    app: AutoCompleteOptionType.isRequired,
    source: AutoCompleteOptionType.isRequired,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    dateRange: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isLoadingMetrics: PropTypes.bool,
    isLoadingCohorts: PropTypes.bool,
};

export default CreativePopup;
